import ArcsTokenIcon from '@/components/ui/ArcsTokenIcon'
import { Icon } from '@components/icons/Icon'
import { MainLayout } from '@components/layouts/MainLayout'
import { useWallet } from '@components/providers/WalletProvider'
import { QrCodeDisplay } from '@components/ui/QrCodeDisplay'
import { motion } from 'framer-motion'
import * as QRCode from 'qrcode'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

const QR_FOREGROUND = '#000000'
const QR_BACKGROUND = '#ffffff'

const formatAddress = (address: string) => {
  if (!address) return ''
  return address.trim()
}

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value)
    toast.success('ウォレットアドレスをコピーしました')
  } catch (_error) {
    const textArea = document.createElement('textarea')
    textArea.value = value
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
      toast.success('ウォレットアドレスをコピーしました')
    } catch (fallbackError) {
      toast.error('コピーに失敗しました')
      console.error('Failed to copy clipboard', fallbackError)
    } finally {
      document.body.removeChild(textArea)
    }
  }
}

export const QrCode: React.FC = () => {
  const [_entered, setEntered] = useState(false)
  const { smartWalletAddress, walletError, isSmartWalletReady } = useWallet()
  const location = useLocation()
  const { from } = (location.state as { from?: string } | null) ?? {}

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const address = smartWalletAddress ?? ''
  const formattedAddress = useMemo(() => formatAddress(address), [address])
  const addressLabel =
    smartWalletAddress && isSmartWalletReady ? 'スマートウォレットアドレス' : 'ウォレットアドレス'
  const isLoading = !walletError && !address

  const handleCopy = () => {
    if (!address) return
    void copyToClipboard(address)
  }

  const handleDownload = async () => {
    if (!address) return
    try {
      const dataUrl = await QRCode.toDataURL(address, {
        margin: 0,
        width: 640,
        color: {
          dark: QR_FOREGROUND,
          light: QR_BACKGROUND,
        },
      })

      const link = document.createElement('a')
      link.href = dataUrl
      link.download = 'arcs-wallet-qr.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('QRコードをダウンロードしました')
    } catch (error) {
      toast.error('QRコードのダウンロードに失敗しました')
      console.error('Failed to download QR code', error)
    }
  }

  const transition = { duration: 0.32, ease: [0.4, 0, 0.2, 1] as const }
  const initial = from?.startsWith('/qr')
    ? { x: from === '/qr/scan' ? '100%' : '-100%', opacity: 0 }
    : { x: 0, opacity: 1 }

  return (
    <MainLayout
      activeMain={null}
      secondaryLayout="qr"
      outerClassName="bg-[#05070B]"
      className="bg-[#05070B] text-white"
      contentClassName="flex flex-col px-6 pb-10 pt-6"
      bottomNavScrolledClassName="supports-[backdrop-filter]:backdrop-blur-[1px]"
      header={{
        enabled: true,
        title: '受け取る',
        leftIcon: null,
        rightIcon: null,
        rightSlot: (
          <button
            type="button"
            aria-label="共有"
            disabled={!address}
            onClick={handleDownload}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#2859C5] shadow-[0_8px_16px_rgba(40,89,197,0.25)] transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon content="Share" className="size-6" decorative />
          </button>
        ),
        titleClassName: 'text-white font-semibold',
        className: 'flex h-16 w-full items-center justify-between',
        containerClassName: 'pt-6 bg-transparent',
        offset: 104,
        scrolledClassName: 'supports-[backdrop-filter]:backdrop-blur-[1px]',
      }}
    >
      <motion.div
        className="flex flex-1 flex-col"
        initial={initial}
        animate={{ x: 0, opacity: 1 }}
        transition={transition}
      >
        <div className="flex flex-1 flex-col items-center gap-8 text-center justify-center">
          <div className="flex flex-col items-center gap-5">
            <div className="relative rounded-[32px] border bg-white p-5">
              {address ? (
                <div className="relative flex items-center justify-center">
                  <QrCodeDisplay
                    value={address}
                    size={216}
                    margin={0}
                    foregroundColor={QR_FOREGROUND}
                    backgroundColor={QR_BACKGROUND}
                    ariaLabel={`${addressLabel}のQRコード`}
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex size-[60px] items-center justify-center rounded-[34px] bg-white">
                      <ArcsTokenIcon className="size-9" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-[216px] w-[216px] items-center justify-center">
                  <div className="h-24 w-24 animate-spin rounded-full border-4 border-white/20 border-t-transparent" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h1 className="text-[20px] font-semibold tracking-[-0.4px]">あなたのQRコード</h1>
              <p className="text-[15px] leading-relaxed text-white/70">
                QRコードを相手にスキャンしてもらうことでトークンを受け取ることができます
              </p>
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-5">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!address}
              className="flex w-full max-w-[480px] items-center justify-center gap-3 rounded-lg bg-[#1C2127] px-4 py-3 text-sm font-semibold text-white transition md:hover:bg-[#232933] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {walletError ? (
                <span className="text-[13px] font-normal text-[#F87171]">{walletError}</span>
              ) : (
                <span className="font-mono text-sm tracking-[0.18em] text-white/80 overflow-hidden break-words text-left">
                  {isLoading ? 'ウォレットアドレスを取得しています…' : formattedAddress}
                </span>
              )}
              <Icon content="Copy" className="size-[16px]" decorative />
            </button>
          </div>
        </div>
      </motion.div>
    </MainLayout>
  )
}

export default QrCode
